import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import {
  ActiveProblemPrmFindControlDto,
  AssessmentsUpdatedDto,
  Bridge,
  RootEventSink,
  OpenWindowEventArgs, OpenWindowType,
  PredefinedUserSettingValue, LinkToOrderDto, GuidStrValue
} from 'webcommon/shared';

// events that can be fired from anywhere with GenericEventManager
// ex: some nested component that can be used in many different areas of the app
export interface GlobalBridgeEventTypes {
  // patient-dashboard
  'assessmentUpdated': AssessmentsUpdatedDto;
  'closePatientDashboard': { Message: string | null; AppointmentId: string | null; PatientId: GuidStrValue; VisitId: string | null };
  'getPredefinedUserSetting': { name: string };
  'openAssessments': { visitId: string; visitAssessmentFormId: string; addAssessmentFormId: string | null };
  'openAttachment': { attachmentId: string };
  'openFindProblem': { InstanceId: number | null };
  'predefinedUserSettingListener': PredefinedUserSettingValue;
  'problems-selection-from-prm': ActiveProblemPrmFindControlDto | null;
  'registerLock': { InstanceId: string };
  'review-related-visits': { Pk: GuidStrValue; PatientId: GuidStrValue; VisitId: GuidStrValue | null };
  'display-info-web-page': { Pk: string };
  'updateBatch': { ARBatchId: string };
  'userSettingListener': string;
  'userSettingRequest': { prmPath: string; attribute: string };
  'userSettingSet': { prmPath: string; attribute: string; value: string };
  'userSettingSetBool': boolean;
  'visitIdChangedFND': { InstanceId: string };
  'medicationImportedToHistory': { patientId?: string; visitId?: string };

  // fire open events to bridge
  // we could maybe allow all these events if this union becomes too long to maintain
  // with OpenWindowEventArgs<OpenWindowType>
  'open': OpenWindowEventArgs<
    OpenWindowType.importMedicationHistory | OpenWindowType.previewDocumentsAndReports | OpenWindowType.trackPatientResults
    | OpenWindowType.newPatientMessage | OpenWindowType.existingMessage
  >;

  'preloadEvent': {
    attachmentUid: string;
    body: string;
    // for some reason, this event was also previously passing patientUid, even though it was never used in the C#
  };
  'preloadResult': {
    hasMedications: boolean;
    hasAllergies: boolean;
    hasProblems: boolean;
  };
  'findOutstandingOrdersListener': LinkToOrderDto;
  'findOutstandingOrders': {
    attachmentUid: string;
    orders?: GuidStrValue[];
  };
  'refreshMessageCenter': null;
  'attachmentApprovalMessageChange': {
    messageId: string;
    patientId?: string;
  };
  'openImportMedications': {
    attachmentUid: string;
    body: string;
    patientUid: string;
  };
  'openImportAllergies': {
    attachmentUid: string;
    body: string;
    patientUid: string;
  };
  'openImportProblems': {
    attachmentUid: string;
    body: string;
    patientUid: string;
  };
  'sendDocument': {
    attachmentUid: string;
  };
  'lockStarted': {
    lockUid: string;
  };
}


// todo: restrict this import with eslint once usages are replaced with GenericEventManager
// todo: delete, or privatize addEventListener, and removeEventListener, so they don't accidentally get used
@Injectable({
  providedIn: 'root',
})
export class BridgeService implements Bridge, RootEventSink {
  private bridge: Bridge | null = null;

  private _canHandleEvents = false;
  public get canHandleEvents(): boolean {
    return this._canHandleEvents;
  }

  public loadConfiguration(configuration: Bridge): void {
    if (this.bridge !== null) {
      throw new Error('bridge has already been set');
    }
    this.bridge = configuration;
    this._canHandleEvents = configuration.canHandleEvents;
  }

  private validateConfiguration(): Bridge {
    if (this.bridge === null) {
      throw new Error('bridge has not been set');
    }
    return this.bridge;
  }

  public hasConfiguration(): boolean {
    return this.bridge !== null;
  }

  public fireEvent(eventName: string, data: any = null): void {
    const bridge = this.validateConfiguration();
    bridge.fireEvent(eventName, data === null ? {} : data);
  }

  addEventListener(eventName: string, callbackFn: (data: any) => void): void {
    const bridge = this.validateConfiguration();
    bridge.addEventListener(eventName, callbackFn);
  }

  removeEventListener(eventName: string, callbackFn: (data: any) => void): void {
    const bridge = this.validateConfiguration();
    bridge.removeEventListener(eventName, callbackFn);
  }

  on<T>(eventName: string): Observable<T> {
    const bridge = this.validateConfiguration();
    return bridge.on<T>(eventName);
  }

  once<T>(eventName: string): Observable<T> {
    const bridge = this.validateConfiguration();
    return bridge.once<T>(eventName);
  }
}
